import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CommTemplatesModule() {
  const [templates, setTemplates] = useState({});
  const [copied, setCopied] = useState(null);
  const [hidden, setHidden] = useState([]);

  const loadHidden = async () => {
    try {
      const { data } = await api.get("/comm-templates/hidden/list");
      setHidden(data.hidden || []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/comm-templates");
        setTemplates(data.templates || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      }
    })();
    loadHidden();
  }, []);

  const copyText = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Texte copié");
    setTimeout(() => setCopied(null), 2000);
  };

  const deleteTemplate = async (key, title) => {
    if (!window.confirm(`Supprimer définitivement la communication « ${title} » ? Elle n'apparaîtra plus ici.`)) return;
    try {
      await api.delete(`/comm-templates/${key}`);
      setTemplates((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success("Communication supprimée");
      loadHidden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const restoreTemplate = async (key) => {
    try {
      await api.post(`/comm-templates/${key}/restore`);
      const { data } = await api.get("/comm-templates");
      setTemplates(data.templates || {});
      loadHidden();
      toast.success("Communication restaurée ✅");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restauration impossible");
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="comm-templates-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-400 tracking-tight">
          📨 Templates Communication — prêts à copier
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          SMS · Facebook · Vidéo · Email — séquence d'exécution recommandée
        </p>
      </div>

      {/* CALENDAR DOWNLOAD */}
      <section className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/40 rounded-xl p-5">
        <h2 className="text-lg font-bold text-emerald-300 mb-2">📅 Ajoute tout dans ton agenda</h2>
        <p className="text-sm text-zinc-300 mb-3">
          Télécharge le fichier <code className="text-emerald-300">.ics</code> ci-dessous, puis ouvre-le sur ton iPhone/Android.
          <strong> Toutes tes tâches</strong> de la semaine (email accountability, audit, SMS, post Facebook) +
          la <strong>vidéo personnelle mi-août</strong> seront ajoutées automatiquement avec rappels mobiles.
        </p>
        <div className="flex gap-2 flex-wrap">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/calendar/kaizen-plan.ics`}
            download="kaizen-plan-ralph.ics"
            data-testid="download-ics-btn"
            className="px-4 py-2 rounded bg-emerald-500 text-zinc-950 font-semibold hover:bg-emerald-400"
          >
            📥 Télécharger mon agenda Kaizen (.ics)
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/calendar/kaizen-plan.ics`}
            target="_blank"
            rel="noreferrer"
            data-testid="view-ics-btn"
            className="px-4 py-2 rounded bg-zinc-800 text-zinc-200 font-semibold hover:bg-zinc-700"
          >
            👁️ Voir le contenu avant
          </a>
        </div>
        <div className="text-xs text-zinc-500 mt-3">
          📱 Sur iPhone : ouvre le fichier → "Ajouter à Calendrier" · Sur Android : ouvre → choisit Google Calendar
        </div>
      </section>

      {/* SEQUENCE */}
      <div className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 border border-amber-500/30 rounded-lg p-5">
        <h2 className="text-lg font-bold text-amber-300 mb-3">🎯 Séquence d'exécution recommandée</h2>
        <ol className="text-sm text-zinc-300 space-y-2 list-decimal list-inside">
          <li>
            <strong>SEMAINE 1 (cette semaine)</strong> · Envoyer l'email "Accountability" déjà dans Gmail
            Brouillons + Audit Membres Actifs en parallèle
          </li>
          <li>
            <strong>SEMAINE 1-2</strong> · Lancer SMS court aux parents (Programme Été)
          </li>
          <li>
            <strong>SEMAINE 2</strong> · Publier le post Facebook Été Jeunes
          </li>
          <li>
            <strong>SEMAINE 4-8</strong> · Collecter présences réelles + relancer les "En danger"
          </li>
          <li className="text-amber-300">
            <strong>MI-AOÛT → SEPTEMBRE</strong> · TOURNER la vidéo personnelle "Pourquoi j'ai créé ce dojo" (PAS avant)
          </li>
        </ol>
      </div>

      {/* TEMPLATES */}
      {Object.entries(templates).map(([key, t]) => (
        <section
          key={key}
          className={`bg-zinc-900 border rounded-xl p-5 ${
            t.warning ? "border-amber-500/40" : "border-zinc-800"
          }`}
          data-testid={`template-${key}`}
        >
          <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-amber-300">{t.title}</h2>
              <div className="text-xs text-zinc-400 mt-1">
                <strong>Canal :</strong> {t.channel} · <strong>Audience :</strong> {t.audience}
              </div>
              {t.best_publish_date && (
                <div className="text-xs text-emerald-300 mt-1">
                  📅 <strong>À publier :</strong> {t.best_publish_date}
                </div>
              )}
              {t.best_time && (
                <div className="text-xs text-zinc-400 mt-1">
                  ⏰ <strong>Meilleur moment :</strong> {t.best_time}
                </div>
              )}
              {t.duration && (
                <div className="text-xs text-zinc-400 mt-1">⏱️ <strong>Durée :</strong> {t.duration}</div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => copyText(key, t.body)}
              data-testid={`copy-${key}`}
              className={`px-4 py-2 rounded font-semibold text-sm ${
                copied === key
                  ? "bg-emerald-500 text-zinc-950"
                  : "bg-amber-500 text-zinc-950 hover:bg-amber-400"
              }`}
            >
              {copied === key ? "✓ Copié" : "📋 Copier le texte"}
            </button>
            <button
              onClick={() => deleteTemplate(key, t.title)}
              data-testid={`delete-${key}`}
              title="Supprimer cette communication"
              className="px-3 py-2 rounded font-semibold text-sm bg-zinc-800 text-rose-300 hover:bg-rose-900/50 border border-rose-500/30"
            >
              🗑️ Supprimer
            </button>
            </div>
          </div>

          {t.warning && (
            <div className="bg-rose-500/10 border border-rose-500/40 text-rose-200 text-sm rounded p-3 mb-3">
              {t.warning}
            </div>
          )}

          <div className="bg-zinc-950 border border-zinc-800 rounded p-4 text-sm text-zinc-200 whitespace-pre-wrap font-mono leading-relaxed">
            {t.body}
          </div>

          {t.tone && (
            <div className="text-xs text-zinc-500 italic mt-2">
              <strong>Ton :</strong> {t.tone}
            </div>
          )}
          {t.media_suggestion && (
            <div className="text-xs text-zinc-400 mt-2">
              <strong>📸 Média recommandé :</strong> {t.media_suggestion}
            </div>
          )}
          {t.production_tips && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-amber-300 mb-1">🎬 Conseils production :</div>
              <ul className="text-xs text-zinc-400 space-y-0.5 list-disc list-inside">
                {t.production_tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ))}

      {/* GMAIL DRAFT LINK */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h2 className="text-lg font-bold text-amber-300 mb-2">📬 Email accountability</h2>
        <p className="text-sm text-zinc-300 mb-3">
          Le brouillon "Nouvelle structure, nouveau départ" est <strong>déjà créé dans Gmail</strong>.
          Va vérifier, ajoute la liste BCC de tes parents/membres, puis envoie.
        </p>
        <a
          href="https://mail.google.com/mail/u/impactkb.bjj@gmail.com/#drafts"
          target="_blank"
          rel="noreferrer"
          data-testid="open-gmail-drafts"
          className="inline-block px-4 py-2 rounded bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400"
        >
          📂 Ouvrir mes brouillons Gmail
        </a>
      </div>

      {/* COMMUNICATIONS SUPPRIMÉES — restaurables */}
      {hidden.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="hidden-templates-section">
          <h2 className="text-lg font-bold text-zinc-300 mb-1">🗑️ Communications supprimées</h2>
          <p className="text-xs text-zinc-500 mb-3">Tu peux réafficher une communication supprimée par erreur.</p>
          <ul className="space-y-2">
            {hidden.map((h) => (
              <li key={h.key} className="flex items-center justify-between gap-3 bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{h.title}</div>
                  {h.channel && <div className="text-[11px] text-zinc-500">{h.channel}</div>}
                </div>
                <button onClick={() => restoreTemplate(h.key)} data-testid={`restore-${h.key}`}
                  className="px-3 py-1.5 rounded text-sm font-semibold bg-emerald-600/80 hover:bg-emerald-600 text-white whitespace-nowrap">
                  ↩️ Restaurer
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
